/**
 * lib/bankFile.ts
 * ------------------------------------------------------------------
 * BANK PAYMENT FILES — written for two callers: payment-voucher batches
 * (which use it now) and payroll salary runs (which do not yet export a file
 * at all). Two divergent bank exports is the failure mode this exists to
 * prevent: the day FNB changes a column, it must change in ONE place, so the
 * payroll exporter belongs here when it is written rather than beside it.
 *
 * A bank is a CONFIG ENTRY, not a code change — the same pattern as the
 * reconciliation column mapping and the PAYE tax tables. Adding Access Bank
 * means adding a BankFormat, not editing an exporter.
 *
 * SANITISATION IS THE POINT. The single most common cause of a rejected
 * Botswana bulk-payment file is a character the bank will not accept: an
 * apostrophe or an ampersand in a beneficiary name bounces the WHOLE file, not
 * just its row. "Kgosi's Hardware" is the classic. Sanitisation happens at
 * EXPORT, never at capture — the supplier's real name stays intact in the
 * register and on the PDF; only the bank's copy is flattened.
 * ------------------------------------------------------------------ */

/** Beneficiary banking details — the shape payroll and suppliers must share. */
export interface BankAccountDetails {
  bankName?: string;
  accountName?: string;
  accountNumber?: string;
  branchCode?: string;
  /** The supplier register calls it `branch`; payroll calls it `branchCode`.
   *  Both are read, because renaming a field on either side to satisfy an
   *  exporter is how a live record loses its banking. */
  branch?: string;
  /** Botswana banks distinguish these on bulk files; optional so no existing
   *  payroll profile breaks by omitting it. */
  accountType?: 'current' | 'savings' | 'transmission';
}

export type BankFieldKey =
  | 'accountName'
  | 'accountNumber'
  | 'bankName'
  | 'branchCode'
  | 'accountType'
  | 'amount'
  | 'reference'
  | 'date'
  | 'sequence';

export interface BankFormat {
  id: string;
  label: string;
  delimiter: ',' | '\t' | '|';
  hasHeaderRow: boolean;
  columns: { key: BankFieldKey; header: string }[];
  /** 'YYYYMMDD' | 'DDMMYYYY' | 'YYYY-MM-DD' */
  dateFormat: string;
  /** 'decimal' → 1234.50 · 'cents' → 123450 (no separator) */
  amountFormat: 'decimal' | 'cents';
  maxRefLength: number;
  accountNumberLength?: { min: number; max: number };
  branchCodeLength?: number;
  trailerRow?: 'none' | 'countAndTotal';
}

/** One payment inside a file — payroll net pay or a voucher total. */
export interface BankFileRow {
  payeeName: string;
  bank: BankAccountDetails;
  amount: number;
  /** What the beneficiary sees on their statement. Truncated per format. */
  reference: string;
}

/* ============================ sanitisation ============================ */

/**
 * Restrict text to the character set every Botswana bank accepts: A–Z, a–z,
 * 0–9, space, hyphen, full stop, slash. Everything else (apostrophes,
 * ampersands, commas, accents, punctuation) is dropped or folded, runs of
 * whitespace collapse, and the result is trimmed and cut to `max`.
 */
export function sanitizeBankText(s: string, max: number): string {
  const folded = (s ?? '')
    // NFD splits an accented letter into base + combining mark, and the
    // whitelist below then drops the mark — so "Ramotswé" exports as "Ramotswe"
    // rather than losing the whole letter.
    .normalize('NFD')
    .replace(/&/g, ' and ')            // the one substitution worth making
    .replace(/[^A-Za-z0-9 \-./]/g, '') // everything else the bank rejects
    .replace(/\s+/g, ' ')
    .trim();
  return max > 0 ? folded.slice(0, max) : folded;
}

/** Digits only — account and branch numbers must never carry spaces or dashes. */
export const digitsOnly = (s: string): string => (s ?? '').replace(/\D/g, '');

/* ============================ formats ============================ */

/**
 * A conservative, widely-accepted layout used until the bank supplies its real
 * spec (open decision 3). Deliberately plain CSV: name, account, branch, amount,
 * reference.
 */
export const GENERIC_CSV: BankFormat = {
  id: 'generic-csv',
  label: 'Generic CSV — name, account, branch, amount, reference',
  delimiter: ',',
  hasHeaderRow: true,
  columns: [
    { key: 'accountName', header: 'Account Name' },
    { key: 'accountNumber', header: 'Account Number' },
    { key: 'bankName', header: 'Bank' },
    { key: 'branchCode', header: 'Branch Code' },
    { key: 'amount', header: 'Amount' },
    { key: 'reference', header: 'Reference' },
  ],
  dateFormat: 'YYYY-MM-DD',
  amountFormat: 'decimal',
  maxRefLength: 20,
  accountNumberLength: { min: 6, max: 20 },
  trailerRow: 'none',
};

/** FNB Botswana "Pay Multiple Beneficiaries". Column set per the standard
 *  template; confirm against the bank's own spec before the first live run. */
export const FNB_BW: BankFormat = {
  id: 'fnb-bw-pmb',
  label: 'FNB Botswana — Pay Multiple Beneficiaries',
  delimiter: ',',
  hasHeaderRow: true,
  columns: [
    { key: 'sequence', header: 'No' },
    { key: 'accountName', header: 'Beneficiary Name' },
    { key: 'accountNumber', header: 'Account Number' },
    { key: 'branchCode', header: 'Branch Code' },
    { key: 'accountType', header: 'Account Type' },
    { key: 'amount', header: 'Amount' },
    { key: 'reference', header: 'Their Reference' },
  ],
  dateFormat: 'YYYYMMDD',
  amountFormat: 'decimal',
  maxRefLength: 20,
  accountNumberLength: { min: 8, max: 16 },
  branchCodeLength: 6,
  trailerRow: 'countAndTotal',
};

export const BANK_FORMATS: BankFormat[] = [GENERIC_CSV, FNB_BW];
export const bankFormatById = (id: string): BankFormat =>
  BANK_FORMATS.find((f) => f.id === id) ?? GENERIC_CSV;

/* ============================ validation ============================ */

export interface BankRowProblem {
  index: number;
  payeeName: string;
  field: 'accountNumber' | 'branchCode' | 'accountName' | 'amount' | 'reference';
  message: string;
  /** A blocker stops the export; a warning is shown and can be proceeded past. */
  severity: 'block' | 'warn';
}

/**
 * Validate rows BEFORE writing a file. This is where the real value sits — the
 * bank's rejection arrives a day later and by then the batch is "sent".
 */
export function validateBankRows(rows: BankFileRow[], fmt: BankFormat): BankRowProblem[] {
  const out: BankRowProblem[] = [];
  rows.forEach((r, index) => {
    const name = r.payeeName || '(unnamed)';
    const acct = digitsOnly(r.bank.accountNumber ?? '');
    const branch = digitsOnly(r.bank.branchCode ?? r.bank.branch ?? '');

    if (!acct) {
      out.push({ index, payeeName: name, field: 'accountNumber', message: 'No account number on file', severity: 'block' });
    } else if (fmt.accountNumberLength && (acct.length < fmt.accountNumberLength.min || acct.length > fmt.accountNumberLength.max)) {
      out.push({
        index, payeeName: name, field: 'accountNumber', severity: 'block',
        message: `Account number is ${acct.length} digits; ${fmt.label} expects ${fmt.accountNumberLength.min}–${fmt.accountNumberLength.max}`,
      });
    }

    if (fmt.branchCodeLength) {
      if (!branch) {
        out.push({ index, payeeName: name, field: 'branchCode', message: 'No branch code on file', severity: 'block' });
      } else if (branch.length !== fmt.branchCodeLength) {
        out.push({
          index, payeeName: name, field: 'branchCode', severity: 'block',
          message: `Branch code is ${branch.length} digits; ${fmt.label} expects ${fmt.branchCodeLength}`,
        });
      }
    }

    if (!(r.bank.accountName ?? '').trim() && !name.trim()) {
      out.push({ index, payeeName: name, field: 'accountName', message: 'No account name to pay to', severity: 'block' });
    }
    if (!(Number(r.amount) > 0)) {
      out.push({ index, payeeName: name, field: 'amount', message: 'Amount is zero or negative', severity: 'block' });
    }
    if (r.reference && sanitizeBankText(r.reference, 0).length > fmt.maxRefLength) {
      out.push({
        index, payeeName: name, field: 'reference', severity: 'warn',
        message: `Reference is longer than ${fmt.maxRefLength} characters and will be truncated`,
      });
    }
    if (r.bank.accountName && sanitizeBankText(r.bank.accountName, 0) !== r.bank.accountName.trim()) {
      out.push({
        index, payeeName: name, field: 'accountName', severity: 'warn',
        message: 'Name contains characters the bank rejects; they will be removed in the file',
      });
    }
  });
  return out;
}

export const hasBlockers = (problems: BankRowProblem[]): boolean =>
  problems.some((p) => p.severity === 'block');

/* ============================ rendering ============================ */

function formatDate(iso: string, pattern: string): string {
  const d = iso.slice(0, 10);
  const [y, m, day] = d.split('-');
  if (pattern === 'YYYYMMDD') return `${y}${m}${day}`;
  if (pattern === 'DDMMYYYY') return `${day}${m}${y}`;
  return d;
}

function formatAmount(n: number, mode: 'decimal' | 'cents'): string {
  const v = Math.round((Number(n) || 0) * 100);
  return mode === 'cents' ? String(v) : (v / 100).toFixed(2);
}

const ACCOUNT_TYPE_CODE: Record<NonNullable<BankAccountDetails['accountType']>, string> = {
  current: '1',
  savings: '2',
  transmission: '3',
};

/** Quote a cell only when the delimiter forces it. Sanitisation has already
 *  removed quotes and commas from text fields, so this is belt-and-braces. */
function cell(v: string, delimiter: string): string {
  return v.includes(delimiter) || v.includes('"') || v.includes('\n')
    ? `"${v.replace(/"/g, '""')}"`
    : v;
}

/** Render the payment file. Every text field passes through sanitizeBankText. */
export function buildBankFile(
  rows: BankFileRow[],
  fmt: BankFormat,
  opts: { valueDate?: string } = {},
): string {
  const date = formatDate(opts.valueDate ?? new Date().toISOString(), fmt.dateFormat);
  const lines: string[] = [];

  if (fmt.hasHeaderRow) {
    lines.push(fmt.columns.map((c) => cell(c.header, fmt.delimiter)).join(fmt.delimiter));
  }

  rows.forEach((r, i) => {
    const values = fmt.columns.map((c) => {
      switch (c.key) {
        case 'sequence': return String(i + 1);
        case 'accountName': return sanitizeBankText(r.bank.accountName || r.payeeName, 35);
        case 'accountNumber': return digitsOnly(r.bank.accountNumber ?? '');
        case 'bankName': return sanitizeBankText(r.bank.bankName ?? '', 35);
        case 'branchCode': return digitsOnly(r.bank.branchCode ?? r.bank.branch ?? '');
        case 'accountType': return r.bank.accountType ? ACCOUNT_TYPE_CODE[r.bank.accountType] : '';
        case 'amount': return formatAmount(r.amount, fmt.amountFormat);
        case 'reference': return sanitizeBankText(r.reference, fmt.maxRefLength);
        case 'date': return date;
        default: return '';
      }
    });
    lines.push(values.map((v) => cell(v, fmt.delimiter)).join(fmt.delimiter));
  });

  if (fmt.trailerRow === 'countAndTotal') {
    const total = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
    lines.push(['TRAILER', String(rows.length), formatAmount(total, fmt.amountFormat)].join(fmt.delimiter));
  }

  return lines.join('\r\n');   // banks expect CRLF
}

/** Trigger a browser download of a rendered file. */
export function downloadBankFile(contents: string, fileName: string): void {
  const blob = new Blob([contents], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(a.href);
}
